import { motion } from 'framer-motion';
import { useLanguage } from '@/i18n/LanguageContext';
import { lc } from '@/i18n/copy';
import { UserCheck, Sparkles, Send } from 'lucide-react';

const copy = {
  en: {
    heading: 'Say what matters while they\'re still here.',
    steps: [
      { num: '1', icon: 'user', title: 'Choose who it\'s for', desc: 'Select the person and type of message.' },
      { num: '2', icon: 'spark', title: 'JacKey helps you begin', desc: 'AI guidance helps unlock the right words.' },
      { num: '3', icon: 'send', title: 'Send something meaningful', desc: 'Your message becomes a lasting moment.' },
    ],
  },
  es: {
    heading: 'Di lo que importa mientras aún están aquí.',
    steps: [
      { num: '1', icon: 'user', title: 'Elige para quién es', desc: 'Selecciona la persona y el tipo de mensaje.' },
      { num: '2', icon: 'spark', title: 'JacKey te ayuda a empezar', desc: 'La guía de IA ayuda a desbloquear las palabras correctas.' },
      { num: '3', icon: 'send', title: 'Envía algo significativo', desc: 'Tu mensaje se convierte en un momento duradero.' },
    ],
  },
  pt: {
    heading: 'Diga o que importa enquanto ainda estão aqui.',
    steps: [
      { num: '1', icon: 'user', title: 'Escolha para quem é', desc: 'Selecione a pessoa e o tipo de mensagem.' },
      { num: '2', icon: 'spark', title: 'JacKey ajuda você a começar', desc: 'A orientação de IA ajuda a desbloquear as palavras certas.' },
      { num: '3', icon: 'send', title: 'Envie algo significativo', desc: 'Sua mensagem se torna um momento duradouro.' },
    ],
  },
  fr: {
    heading: 'Dites ce qui compte tant qu\'ils sont encore là.',
    steps: [
      { num: '1', icon: 'user', title: 'Choisissez à qui', desc: 'Sélectionnez la personne et le type de message.' },
      { num: '2', icon: 'spark', title: 'JacKey vous aide à commencer', desc: 'L\'IA vous aide à trouver les bons mots.' },
      { num: '3', icon: 'send', title: 'Envoyez quelque chose de significatif', desc: 'Votre message devient un moment durable.' },
    ],
  },
  ar: { heading: 'قل ما يهم بينما لا يزالون هنا.', steps: [{ num: '1', icon: 'user', title: 'اختر لمن', desc: 'حدد الشخص ونوع الرسالة.' }, { num: '2', icon: 'spark', title: 'JacKey يساعدك على البدء', desc: 'توجيه الذكاء الاصطناعي يساعد في إيجاد الكلمات المناسبة.' }, { num: '3', icon: 'send', title: 'أرسل شيئًا ذا معنى', desc: 'رسالتك تصبح لحظة دائمة.' }] },
  ja: { heading: 'まだいるうちに大切なことを伝えましょう。', steps: [{ num: '1', icon: 'user', title: '誰に伝えるか選ぶ', desc: '相手とメッセージの種類を選択します。' }, { num: '2', icon: 'spark', title: 'JacKeyが手助け', desc: 'AIが適切な言葉を見つけるお手伝いをします。' }, { num: '3', icon: 'send', title: '意味のあるものを送る', desc: 'あなたのメッセージは永続的な瞬間になります。' }] },
  ko: { heading: '아직 여기 있을 때 중요한 것을 말하세요.', steps: [{ num: '1', icon: 'user', title: '누구를 위한 것인지 선택', desc: '사람과 메시지 유형을 선택합니다.' }, { num: '2', icon: 'spark', title: 'JacKey가 시작을 도와줍니다', desc: 'AI 안내가 적절한 말을 찾도록 도와줍니다.' }, { num: '3', icon: 'send', title: '의미 있는 것을 보내세요', desc: '당신의 메시지는 지속적인 순간이 됩니다.' }] },
  id: { heading: 'Katakan yang penting selagi mereka masih di sini.', steps: [{ num: '1', icon: 'user', title: 'Pilih untuk siapa', desc: 'Pilih orang dan jenis pesan.' }, { num: '2', icon: 'spark', title: 'JacKey membantu Anda memulai', desc: 'Panduan AI membantu menemukan kata yang tepat.' }, { num: '3', icon: 'send', title: 'Kirim sesuatu yang bermakna', desc: 'Pesan Anda menjadi momen yang abadi.' }] },

  hi: {
    heading: 'जब वे अभी भी यहाँ हैं तो जो मायने रखता है उसे कहें।',
    steps: [
      { num: '1', icon: 'user', title: 'Choose who it\'s for', desc: 'Select the person and type of message.' },
      { num: '2', icon: 'spark', title: 'JacKey helps you begin', desc: 'AI guidance helps unlock the right words.' },
      { num: '3', icon: 'भेजें', title: 'कुछ सार्थक भेजें', desc: 'आपका संदेश एक स्थायी क्षण बन जाता है।' },
    ],
  },
};

const iconMap = {
  user: UserCheck,
  spark: Sparkles,
  send: Send,
};

export function SolutionSection() {
  const { language } = useLanguage();
  const t = lc(copy, language);

  return (
    <section id="how-it-works" className="py-20 md:py-28">
      <div className="container max-w-4xl mx-auto text-center">
        <motion.h2
          className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold billboard-flash mb-16 !leading-tight"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          {t.heading}
        </motion.h2>

        <div className="grid md:grid-cols-3 gap-8 md:gap-10">
          {t.steps.map((step, i) => {
            const Icon = iconMap[step.icon as keyof typeof iconMap];
            return (
              <motion.div
                key={i}
                className="relative flex flex-col items-center gap-5 p-8 rounded-2xl bg-card border border-border/30 shadow-sm"
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.14 }}
              >
                <div className="h-14 w-14 rounded-2xl bg-primary/10 flex items-center justify-center">
                  <Icon className="h-7 w-7 text-primary" />
                </div>
                <span className="absolute -top-3 -left-3 h-8 w-8 rounded-full bg-primary text-primary-foreground text-sm font-bold flex items-center justify-center shadow-md">
                  {step.num}
                </span>
                <h3 className="text-lg font-semibold text-foreground">{step.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{step.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
